import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "child-psychology", label: "Child Psychology" },
  { id: "blog", label: "Blog" },
  { id: "testimonials", label: "Testimonials" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const [open, setOpen] = useState(false);
  const onBlogPage = location.pathname.startsWith("/blog");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 80);
      // active section detection
      const offsets = links
        .map((l) => {
          const el = document.getElementById(l.id);
          if (!el) return null;
          const rect = el.getBoundingClientRect();
          return { id: l.id, top: rect.top };
        })
        .filter(Boolean);
      const current = offsets
        .filter((o) => o.top <= 120)
        .sort((a, b) => b.top - a.top)[0];
      if (current) setActive(current.id);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const scrollTo = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollTo, 100);
    } else {
      scrollTo();
    }
    setOpen(false);
  };

  return (
    <motion.nav
      data-testid="main-navbar"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background:
          scrolled || onBlogPage ? "rgba(255,255,255,0.95)" : "transparent",
        backdropFilter: scrolled || onBlogPage ? "blur(10px)" : "none",
        boxShadow:
          scrolled || onBlogPage ? "0 6px 24px rgba(31,51,41,0.08)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <button
          onClick={() => go("home")}
          data-testid="navbar-logo"
          className="font-display font-bold text-xl md:text-2xl tracking-wide"
          style={{
            color:
              scrolled || onBlogPage ? "var(--color-text-dark)" : "#ffffff",
          }}
        >
          DR. FIZA HASNY
        </button>

        {/* Desktop */}
        <ul className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-body text-sm uppercase tracking-widest transition-colors"
                style={{
                  color:
                    active === l.id
                      ? "var(--color-accent)"
                      : scrolled || onBlogPage
                        ? "var(--color-text-dark)"
                        : "#ffffff",
                  fontWeight: active === l.id ? 600 : 400,
                }}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-md"
          style={{
            color:
              scrolled || onBlogPage ? "var(--color-text-dark)" : "#ffffff",
          }}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-white border-t border-[#e3efe9]"
          >
            <ul className="px-6 py-4 flex flex-col gap-3">
              {links.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`nav-mobile-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="w-full text-left font-body py-2 uppercase tracking-widest text-sm"
                    style={{
                      color:
                        active === l.id
                          ? "var(--color-accent)"
                          : "var(--color-text-dark)",
                      fontWeight: active === l.id ? 600 : 400,
                    }}
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
